// frontend/src/modules/client/components/BandeauReservation.jsx
// ✅ VERSION FINALE COMPLÈTE - À déployer
// ✅ FIX 2 : Détection réservation existante + Bannière bleue
import React, { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle, Clock, Edit } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const BandeauReservation = ({ user }) => {  // ← ✅ Ajout prop user
  const navigate = useNavigate();
  const [siteState, setSiteState] = useState(null);
  const [periode, setPeriode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reservationEnCours, setReservationEnCours] = useState(null);  // ← ✅ NOUVEAU

  useEffect(() => {
    loadSiteState();
    // Recharger toutes les 5 minutes
    const interval = setInterval(loadSiteState, 300000);
    return () => clearInterval(interval);
  }, []);

  // ✅ NOUVEAU : Charger réservation en cours du client
  useEffect(() => {
    if (user?.code_client && periode?.id) {
      loadReservationEnCours();
    }
  }, [user, periode]);

  const loadSiteState = async () => {
    try {
      // Charger l'état du site
      const stateResponse = await fetch(`${API_BASE}/site/state`);
      const stateData = await stateResponse.json();
      setSiteState(stateData.data);

      // Charger la période active
      const periodeResponse = await fetch(`${API_BASE}/periodes/active`);
      const periodeData = await periodeResponse.json();
      if (periodeData.success) {
        setPeriode(periodeData.periode || periodeData.data);
      }

      setLoading(false);
    } catch (error) {
      console.error('Erreur chargement état site:', error);
      setLoading(false);
    }
  };

  // ✅ NOUVEAU : Charger la réservation en cours
  const loadReservationEnCours = async () => {
    try {
      const response = await fetch(`${API_BASE}/reservations/client/${user.code_client}`);
      const result = await response.json();
      
      if (result.success && result.data.length > 0) {
        // Trouver la réservation en cours pour cette période
        const resaEnCours = result.data.find(r => 
          r.periode_id === periode.id && 
          ['en_cours', 'confirmee'].includes(r.statut)
        );
        
        if (resaEnCours) {
          console.log('🔔 Réservation en cours détectée:', resaEnCours.numero_reservation);
          setReservationEnCours(resaEnCours);
        }
      }
    } catch (error) {
      console.error('Erreur chargement réservation:', error);
    }
  };

  if (loading) {
    return (
      <div className="bg-gray-100 p-3 text-center">
        <span className="text-gray-500">Chargement...</span>
      </div>
    );
  }

  // Définir les styles selon l'état
  const getStateStyle = () => {
    switch (siteState?.state) {
      case 'ouvert_reservations':
        return {
          bg: 'bg-green-50 border-green-200',
          text: 'text-green-800',
          icon: '🟢',
          label: 'Réservations ouvertes'
        };
      case 'ouvert_modifications':
        return {
          bg: 'bg-yellow-50 border-yellow-200',
          text: 'text-yellow-800',
          icon: '🟡',
          label: 'Juste accessible à la modification'
        };
      case 'consultation_seule':
        return {
          bg: 'bg-red-50 border-red-200',
          text: 'text-red-800',
          icon: '🔴',
          label: 'Réservations fermées'
        };
      default:
        return {
          bg: 'bg-gray-50 border-gray-200',
          text: 'text-gray-800',
          icon: 'ℹ️',
          label: 'Informations non disponibles'
        };
    }
  };

  const style = getStateStyle();

  // Formater les dates de la période
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  return (
    <div>
      {/* Bandeau période et état */}
      <div className={`border-b-2 ${style.bg} ${style.text}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            {/* Période */}
            <div className="mb-2 md:mb-0">
              <div className="flex items-center">
                <span className="text-2xl mr-2">📅</span>
                <div>
                  {periode ? (
                    <>
                      <span className="font-semibold">Semaine de réservation</span>
                      <span className="ml-2">
                        du {formatDate(periode.date_debut_reservations)} au {formatDate(periode.date_fin_reservations)}
                      </span>
                    </>
                  ) : (
                    <span className="font-semibold">Aucune période active</span>
                  )}
                </div>
              </div>
            </div>

            {/* État */}
            <div className="flex items-center">
              <span className="text-2xl mr-2">{style.icon}</span>
              <span className="font-semibold text-lg">{style.label}</span>
            </div>
          </div>

          {/* Message détaillé */}
          {siteState?.message && (
            <div className="mt-2 text-sm">
              {siteState.message}
            </div>
          )}
        </div>
      </div>

      {/* ✅ NOUVEAU : Bandeau réservation en cours */}
      {reservationEnCours && (
        <div className="bg-blue-50 border-b-2 border-blue-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-6 w-6 text-blue-600" />
                <div>
                  <div className="font-semibold text-blue-900">
                    ✅ Vous avez déjà une réservation en cours
                  </div>
                  <div className="text-sm text-blue-700">
                    <span className="font-mono bg-blue-100 px-2 py-0.5 rounded">
                      {reservationEnCours.numero_reservation}
                    </span>
                    <span className="mx-2">•</span>
                    <span>Montant: {Number(reservationEnCours.montant_estime || 0).toFixed(2)}€</span>
                    {reservationEnCours.statut === 'en_cours' && (
                      <>
                        <span className="mx-2">•</span>
                        <span className="text-orange-700">Non confirmée</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Boutons d'action */}
              <div className="flex gap-2">
                <button
                  onClick={() => navigate('/profile')}
                  className="flex items-center gap-1 bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 text-sm font-medium"
                >
                  <Edit className="h-4 w-4" />
                  Voir / Modifier
                </button>
              </div>
            </div>

            {/* ⚠️ Avertissement modification */}
            <div className="mt-2 flex items-start gap-2 bg-yellow-50 border border-yellow-200 rounded-lg p-2">
              <AlertCircle className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-yellow-800">
                <strong>Attention :</strong> Si vous ajoutez de nouveaux produits au panier, 
                ils <strong>remplaceront</strong> votre réservation actuelle. 
                Utilisez le bouton "Voir / Modifier" pour modifier votre réservation existante.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BandeauReservation;
